import Autocomplete from 'react-google-autocomplete';

export interface ParsedAddress {
  line1: string;
  /** Suite/unit (Google `subpremise`), when the selected place includes one. */
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  /** Google already resolved this, so taking it here saves a geocoding call. */
  latitude?: number;
  longitude?: number;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPlaceSelected: (fields: ParsedAddress) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

/**
 * Parses a Google Place result into structured address fields
 */
function parsePlace(place: google.maps.places.PlaceResult): ParsedAddress {
  const components = place.address_components || [];
  const get = (type: string, short = false) => {
    const part = components.find((c) => c.types.includes(type));
    return short ? part?.short_name || '' : part?.long_name || '';
  };
  // Same Basic Data tier as address_components, so this costs nothing extra.
  const point = place.geometry?.location;

  return {
    line1: [get('street_number'), get('route')].filter(Boolean).join(' '),
    // Google returns the unit as `subpremise` (e.g. "2" for "… unit 2").
    line2: get('subpremise'),
    city: get('locality') || get('sublocality') || get('administrative_area_level_2') || '',
    state: get('administrative_area_level_1', true),
    postalCode: get('postal_code'),
    country: get('country', true) || 'USA',
    latitude: point?.lat(),
    longitude: point?.lng(),
  };
}

/**
 * Reusable Google Maps address autocomplete input.
 *
 * Props:
 *   value          — current string value for the address line 1 field
 *   onChange       — standard React input onChange handler (receives event)
 *   onPlaceSelected — called with { line1, city, state, postalCode, country }
 *                     when the user picks an autocomplete suggestion
 *   disabled       — disables the input
 *   placeholder    — input placeholder text
 *   className      — CSS class name (defaults to 'google-address-input')
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onPlaceSelected,
  disabled,
  placeholder,
  className,
}: AddressAutocompleteProps) {
  return (
    <Autocomplete
      apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
      value={value ?? ''}
      onChange={onChange}
      onPlaceSelected={(place) => onPlaceSelected(parsePlace(place))}
      options={{
        types: ['address'],
        componentRestrictions: { country: 'us' },
        fields: ['address_components', 'formatted_address', 'geometry.location'],
      }}
      placeholder={placeholder || 'Start typing address...'}
      className={className || 'google-address-input'}
      disabled={disabled}
    />
  );
}
